/**
 * `/` redirect logic. Picks where to land an authenticated user based
 * on their state:
 *   * Memberships still loading (initial render): brief spinner.
 *   * No memberships: a "nothing to manage" landing.
 *   * Account exists with empty elderName: /welcome to capture it.
 *   * Otherwise: /accounts/:aid/settings — the Settings page renders
 *     the elder-setup form when elderName is empty, and is the
 *     general non-calendar surface otherwise.
 *
 * No signup-grace timer is needed: ensure_user_profile is awaited
 * synchronously by signUpWithEmail / signUpWithGoogle before we
 * navigate here, so the docs are guaranteed to exist server-side by
 * the time we mount. The hooks' own `loading` flags cover the brief
 * window before their listeners deliver the first snapshot.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { useAccount } from '@/hooks/useAccount';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { logOut } from '@/lib/auth';

export function RootRedirect() {
  const auth = useAuth();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships, loading } = useMyMemberships(uid);
  const firstAid = memberships[0]?.id ?? null;
  const { account, loading: accountLoading } = useAccount(firstAid);

  if (loading || (firstAid && (accountLoading || !account))) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        <Spinner />
      </div>
    );
  }

  if (!firstAid) {
    return <NoAccountsLanding />;
  }

  // First sign-in: account exists but elderName not yet captured. Send
  // them through Welcome to collect that, then on to Voice for
  // personalisation. Users who already named their elder skip this
  // entirely. Caregivers arriving via /add-button?code=... bypass
  // this redirect because they hit that route directly.
  if (account && !account.elderName.trim()) {
    return <Navigate to="/welcome" replace />;
  }

  // Default landing: Voice page — the main day-to-day surface.
  void account;
  return <Navigate to={`/accounts/${firstAid}/voice`} replace />;
}

// Reachable while signed in with no memberships — most notably right after
// an admin deletes their only account (delete_account leaves the auth user),
// or after being removed from every shared account. Without an escape hatch
// the user is stranded here: still authenticated, no account, and /login just
// bounces back to `/`. Always offer a sign-out so it's never a dead-end.
function NoAccountsLanding() {
  const [busy, setBusy] = useState(false);
  async function onSignOut() {
    setBusy(true);
    try {
      await logOut();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="max-w-md rounded-card border border-paper-border bg-white p-8 text-center shadow-card">
        <h1 className="text-xl font-semibold text-ink">Nothing to manage yet</h1>
        <p className="mt-2 text-sm text-ink-soft">
          You're not on any shared accounts. If you were expecting an invitation, check your
          email — invites land on a `/invites/&lt;token&gt;` link.
        </p>
        <div className="mt-6">
          <Button variant="secondary" onClick={onSignOut} disabled={busy}>
            {busy ? 'Signing out…' : 'Sign out'}
          </Button>
        </div>
      </div>
    </div>
  );
}
